import { useState, useRef, useEffect } from "react";
import { PackageX, Loader2, CheckCircle2, AlertCircle, ClipboardPaste, ChevronLeft, ChevronRight, Search, Trash2, Info, Ban } from "lucide-react";
import { toast } from "sonner";
import {
  usePackageReturns,
  usePackageReturnsSummary,
  useSuspiciousPackages,
  useScanPackageReturn,
  useLookupPackageReturn,
  useBulkScanPackageReturn,
  type ScanLookup,
  useRecordOrderReturn,
  useUpdatePackageReturn,
  useDeletePackageReturn,
  useCancelReturnOrder,
  type CancelOutcome,
  type SuspiciousPackage,
} from "@/hooks/use-package-returns";
import { useNavigate } from "@tanstack/react-router";
import {
  RETURN_REASONS,
  RETURN_REASON_LABEL,
  RETURN_REASON_BADGE,
  RETURN_REASON_DOT,
  CANCEL_STATUS_LABEL,
  CANCEL_STATUS_BADGE,
  canRetryCancel,
  type PackageReturnReason,
} from "@/lib/package-return-labels";
import { useStores } from "@/hooks/use-stores";
import { useActiveStore } from "@/hooks/use-active-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/orders/status-badge";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { fulfillmentReturnedRoute } from "@/lib/router";

const TABS = [
  { value: "evidencija", label: "Evidencija" },
  { value: "sumnjivi", label: "Sumnjivi" },
] as const;

const SUSPICIOUS_DAYS_OPTIONS = [7, 14, 21, 30];

const SUSPICIOUS_RETURN_REASONS = RETURN_REASONS.filter((r) => r !== "lost");

function toDateInput(d: Date | undefined): string {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function fromDateInput(v: string): Date | undefined {
  if (!v) return undefined;
  const parts = v.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="shrink-0 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{children}</span>
  );
}

function formatOrderDate(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("bs-BA");
}

/** Pregled pronađene pošiljke prije evidentiranja: tri zone — pošiljka (šta je
 *  skenirano), kupac (kome je išlo) i sadržaj (šta bi trebalo biti u kutiji),
 *  a otkupnina stoji sama jer je to iznos koji visi na vraćenom paketu. */
function ScanPreviewCard({
  scan,
  onOpenOrder,
}: {
  scan: ScanLookup;
  onOpenOrder: (orderId: string, storeId: string) => void;
}) {
  const order = scan.order;
  const orderDate = formatOrderDate(order?.orderDate ?? null);
  const address = [
    order?.address,
    [order?.postcode, order?.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="border border-foreground/15 bg-muted/25">
      {/* Pošiljka */}
      <div className="flex items-start justify-between gap-x-4 gap-y-1.5 flex-wrap px-3 py-2 border-b border-foreground/10">
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          <button
            type="button"
            onClick={() => onOpenOrder(scan.orderId!, scan.storeId!)}
            className="text-sm font-medium text-primary hover:underline"
          >
            #{scan.orderId}
          </button>
          {order?.orderStatus && <StatusBadge status={order.orderStatus} />}
          {scan.storeName && <span className="text-xs text-muted-foreground truncate">{scan.storeName}</span>}
          {orderDate && <span className="text-xs text-muted-foreground tabular-nums">{orderDate}</span>}
        </div>
        <div className="flex flex-col items-end shrink-0 leading-tight">
          <span className="font-mono text-xs">{scan.trackingNumber}</span>
          {scan.credentialName && (
            <span className="text-[10px] text-muted-foreground">{scan.credentialName}</span>
          )}
        </div>
      </div>

      {/* Kupac + sadržaj */}
      {order ? (
        <div className="grid sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-foreground/10">
          <div className="px-3 py-2.5 flex flex-col gap-1 min-w-0">
            <FieldLabel>Kupac</FieldLabel>
            <span className="text-sm font-medium truncate">
              {order.customerName || <span className="font-normal text-muted-foreground">Bez imena</span>}
            </span>
            <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs">
              {order.customerPhone && (
                <a href={`tel:${order.customerPhone}`} className="text-primary hover:underline tabular-nums">
                  {order.customerPhone}
                </a>
              )}
              {order.customerEmail && (
                <span className="text-muted-foreground truncate max-w-full">{order.customerEmail}</span>
              )}
            </div>
            {address && <span className="text-xs text-muted-foreground leading-snug">{address}</span>}
          </div>

          <div className="px-3 py-2.5 flex flex-col gap-1 min-w-0">
            <FieldLabel>
              Sadržaj{order.items.length > 0 ? ` · ${order.items.length}` : ""}
            </FieldLabel>
            {order.items.length > 0 ? (
              <ul className="flex flex-col gap-1 text-xs">
                {order.items.map((it, i) => (
                  <li key={i} className="flex gap-2 min-w-0">
                    <span className="tabular-nums text-muted-foreground shrink-0 w-6 text-right">
                      {it.quantity ?? 1}×
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate">{it.name || "—"}</span>
                      {it.sku && <span className="font-mono text-[10px] text-muted-foreground/70">{it.sku}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="text-xs text-muted-foreground">Stavke nisu poznate</span>
            )}
          </div>
        </div>
      ) : (
        <div className="px-3 py-2.5 text-xs text-muted-foreground">
          Podaci o kupcu nisu dostupni — narudžba nije u lokalnom kešu.
        </div>
      )}

      {/* Napomena kupca — puna širina, vezana za dostavu a ne za sadržaj */}
      {order?.customerNote && (
        <div className="flex items-baseline gap-2.5 px-3 py-2 border-t border-foreground/10">
          <FieldLabel>Napomena</FieldLabel>
          <span className="text-xs text-muted-foreground min-w-0">{order.customerNote}</span>
        </div>
      )}

      {/* Otkupnina */}
      {scan.codAmount != null && (
        <div className="flex items-baseline justify-between gap-3 px-3 py-2 border-t border-foreground/10">
          <FieldLabel>Otkupnina</FieldLabel>
          <span className="text-base font-medium tabular-nums leading-none">
            {scan.codAmount.toFixed(2)}{" "}
            <span className="text-xs font-normal text-muted-foreground">{scan.codCurrency ?? "BAM"}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export function ReturnedPackagesPage() {
  const { q: initialQ } = fulfillmentReturnedRoute.useSearch();
  const { data: stores } = useStores();
  const navigate = useNavigate();
  const { storeId: activeStoreId, setStoreId: setActiveStoreId } = useActiveStore();

  const [pageStoreFilter, setPageStoreFilter] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"evidencija" | "sumnjivi">("evidencija");
  const [reasonFilter, setReasonFilter] = useState<string>("all");
  const [search, setSearch] = useState(initialQ ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(initialQ ?? "");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [page, setPage] = useState(1);
  const [scanReason, setScanReason] = useState<PackageReturnReason>("refused");
  const [scanCode, setScanCode] = useState("");
  const [scanNotes, setScanNotes] = useState("");
  const [pendingScan, setPendingScan] = useState<ScanLookup | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkCodes, setBulkCodes] = useState<string[]>([]);
  const [bulkResult, setBulkResult] = useState<{
    linked: { trackingNumber: string; orderId: string; storeName: string | null }[];
    unlinkedCodes: string[];
    duplicates: string[];
  } | null>(null);
  const [suspiciousDays, setSuspiciousDays] = useState(14);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actingCodId, setActingCodId] = useState<string | null>(null);
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  const showAllStores = pageStoreFilter === null;

  const { data: entries, isLoading } = usePackageReturns(pageStoreFilter, {
    reason: reasonFilter === "all" ? undefined : reasonFilter,
    q: debouncedSearch || undefined,
    after: dateFrom ? Math.floor(dateFrom.getTime() / 1000) : undefined,
    before: dateTo ? Math.floor(dateTo.getTime() / 1000) + 86399 : undefined,
    page,
  });
  const { data: summary } = usePackageReturnsSummary(pageStoreFilter);
  const { data: suspicious, isLoading: suspiciousLoading } = useSuspiciousPackages(pageStoreFilter, suspiciousDays);

  const scanReturn = useScanPackageReturn();
  const lookupCode = useLookupPackageReturn();
  const bulkScan = useBulkScanPackageReturn();
  const recordReturn = useRecordOrderReturn();
  const updateReturn = useUpdatePackageReturn();
  const deleteReturn = useDeletePackageReturn();
  const cancelOrder = useCancelReturnOrder();

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Vraćeni paketi nose X ili PPX kodove; OX prefiks postoji samo na otkupninama,
  // pa se iz zalijepljenog OX koda hvata unutrašnji X dio.
  function extractCodes(text: string): string[] {
    const matches = text.match(/(?:PPX|X)\d{9,}/g) || [];
    return [...new Set(matches)];
  }

  function handleBulkTextChange(text: string) {
    setBulkText(text);
    setBulkCodes(extractCodes(text));
    setBulkResult(null);
  }

  // Sken je dvostepen: Enter na kodu pokaže pronađenu narudžbu, drugi Enter
  // (ili klik na Evidentiraj) tek tada upisuje povrat s odabranim razlogom.
  async function handleScanSubmit(e: React.FormEvent) {
    e.preventDefault();
    const code = scanCode.trim();
    if (!code) {
      if (pendingScan && !pendingScan.existing) await handleConfirm();
      return;
    }
    try {
      const result = await lookupCode.mutateAsync(code);
      setPendingScan(result);
      setScanCode("");
      scanInputRef.current?.focus();
    } catch (e: any) {
      toast.error(e?.message || "Greška pri provjeri koda");
      setScanCode("");
      scanInputRef.current?.focus();
    }
  }

  async function handleConfirm() {
    if (!pendingScan) return;
    try {
      const result = await scanReturn.mutateAsync({
        code: pendingScan.trackingNumber,
        reason: scanReason,
        notes: scanNotes,
      });
      const storeLabel = result.entry.storeName ? ` (${result.entry.storeName})` : "";
      const orderLabel = result.entry.orderId ? `#${result.entry.orderId}` : result.entry.trackingNumber;
      toast.success(`${orderLabel}${storeLabel} — evidentiran povrat`);
      reportCancel(result.cancel, orderLabel);
      setPendingScan(null);
      setScanNotes("");
      scanInputRef.current?.focus();
    } catch (e: any) {
      toast.error(e?.message || "Greška pri evidentiranju");
      scanInputRef.current?.focus();
    }
  }

  async function handleBulkSubmit() {
    if (bulkCodes.length === 0) return;
    try {
      const result = await bulkScan.mutateAsync({ codes: bulkCodes, reason: scanReason });
      setBulkResult({
        linked: result.recorded
          .filter((e) => e.orderId)
          .map((e) => ({ trackingNumber: e.trackingNumber, orderId: e.orderId!, storeName: e.storeName })),
        unlinkedCodes: result.unlinkedCodes,
        duplicates: result.duplicates,
      });
      toast.success(`${result.recorded.length} paketa evidentirano`);
      const cancelled = result.cancelCounts?.cancelled ?? 0;
      const failed = result.cancelCounts?.failed ?? 0;
      const paid = result.cancelCounts?.skipped_paid ?? 0;
      if (cancelled) toast.success(`${cancelled} narudžbi otkazano`);
      if (paid) toast.warning(`${paid} plaćenih narudžbi nije otkazano`);
      if (failed) toast.error(`${failed} narudžbi nije uspjelo otkazati — ponovi iz tabele`);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri obradi kodova");
    }
  }

  // Otkazivanje je propratni efekt evidentiranja — javi ga posebnim toastom da
  // operater odmah zna je li narudžba stvarno zatvorena.
  function reportCancel(cancel: CancelOutcome, orderLabel: string) {
    if (cancel.status === "cancelled") toast.success(`${orderLabel} — narudžba otkazana`);
    else if (cancel.status === "failed") toast.error(`${orderLabel} — otkazivanje nije uspjelo: ${cancel.message}`);
    else if (cancel.status === "skipped_paid") toast.warning(`${orderLabel} — plaćena narudžba, nije otkazana`);
  }

  async function handleCancelOrder(id: string, orderId: string | null, force: boolean) {
    if (force && !confirm("Narudžba je plaćena — povrat novca moraš uraditi ručno. Ipak otkazati?")) return;
    setCancelingId(id);
    try {
      const result = await cancelOrder.mutateAsync({ id, force });
      reportCancel(result.cancel, orderId ? `#${orderId}` : "Narudžba");
      if (result.cancel.status === "already_cancelled") toast.info("Narudžba je već bila otkazana");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri otkazivanju narudžbe");
    } finally {
      setCancelingId(null);
    }
  }

  async function handleChangeReason(id: string, reason: PackageReturnReason) {
    try {
      await updateReturn.mutateAsync({ id, reason });
      toast.success("Razlog ažuriran");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri ažuriranju razloga");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Obrisati zapis povrata?")) return;
    setDeletingId(id);
    try {
      await deleteReturn.mutateAsync(id);
      toast.success("Zapis obrisan");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju");
    } finally {
      setDeletingId(null);
    }
  }

  async function handleSuspiciousAction(s: SuspiciousPackage, reason: PackageReturnReason) {
    setActingCodId(s.codId);
    try {
      const result = await recordReturn.mutateAsync({ orderId: s.orderId, storeId: s.storeId, reason });
      toast.success(`#${s.orderId} — povrat evidentiran`);
      reportCancel(result.cancel, `#${s.orderId}`);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri evidentiranju povrata");
    } finally {
      setActingCodId(null);
    }
  }

  // Stranica je cross-store, a detalj narudžbe čita globalno aktivnu trgovinu —
  // prije navigacije prebaci aktivnu trgovinu na onu iz reda i obavijesti korisnika.
  function openOrder(orderId: string, storeId: string) {
    if (storeId !== activeStoreId) {
      setActiveStoreId(storeId);
      const storeName = stores?.find((s: { id: string; name: string }) => s.id === storeId)?.name;
      toast.info(`Trgovina automatski promijenjena na ${storeName ?? storeId}`);
    }
    navigate({ to: "/orders/$orderId", params: { orderId } });
  }

  const totalPages = entries ? Math.ceil(entries.total / 25) : 0;
  const summaryReasons = summary
    ? RETURN_REASONS.filter((r) => (summary.byReason[r] ?? 0) > 0)
    : [];

  return (
    <div className="p-4 flex flex-col gap-4 max-w-5xl">
      {/* Header row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <PackageX className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-medium">Vraćeni paketi</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={pageStoreFilter ?? "all"}
            onValueChange={(v) => {
              setPageStoreFilter(v === "all" ? null : v);
              setPage(1);
            }}
          >
            <SelectTrigger className="h-8 w-[180px] text-xs">
              <SelectValue placeholder="Trgovina" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Sve trgovine</SelectItem>
              {stores?.map((store: { id: string; name: string }) => (
                <SelectItem key={store.id} value={store.id}>
                  {store.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary strip: total + reason distribution bar */}
      {!summary ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <div className="ring-1 ring-foreground/10 p-3 flex flex-col gap-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Ukupno vraćeno</span>
            <span className="text-lg font-medium tabular-nums leading-none">{summary.total}</span>
          </div>
          {summary.total > 0 && (
            <>
              <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label="Raspodjela po razlogu">
                {summaryReasons.map((r) => (
                  <div
                    key={r}
                    className={RETURN_REASON_DOT[r]}
                    style={{ width: `${((summary.byReason[r] ?? 0) / summary.total) * 100}%` }}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {summaryReasons.map((r) => (
                  <span key={r} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", RETURN_REASON_DOT[r])} />
                    {RETURN_REASON_LABEL[r]}
                    <span className="tabular-nums text-foreground">{summary.byReason[r]}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Scanner panel */}
      <div className="border border-foreground/15 p-3 flex flex-col gap-2">
          <form onSubmit={handleScanSubmit} className="flex gap-2">
            <Input
              ref={scanInputRef}
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              placeholder="X017504263 ili PPX017504263"
              className="flex-1 h-10 font-mono"
              autoFocus
            />
            <Button
              type="submit"
              variant="outline"
              className="h-10 px-4 shrink-0"
              disabled={lookupCode.isPending || (!scanCode.trim() && !pendingScan)}
            >
              {lookupCode.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Provjeri"}
            </Button>
            <Button
              type="button"
              variant={bulkOpen ? "secondary" : "ghost"}
              className="h-10 shrink-0 text-xs text-muted-foreground"
              onClick={() => setBulkOpen(!bulkOpen)}
            >
              <ClipboardPaste className="h-3 w-3 mr-1.5" />
              Bulk unos
            </Button>
          </form>

          {pendingScan && (
            <div className="border-t border-foreground/10 pt-2.5 flex flex-col gap-2.5">
              {pendingScan.linked ? (
                <ScanPreviewCard scan={pendingScan} onOpenOrder={openOrder} />
              ) : (
                <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    Kod <span className="font-mono">{pendingScan.trackingNumber}</span> nije pronađen u pošiljkama —
                    možeš ga svejedno evidentirati kao nepovezan povrat.
                  </span>
                </div>
              )}

              {pendingScan.existing ? (
                <div className="flex items-center gap-2 flex-wrap text-xs text-destructive">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Već evidentiran kao vraćen — {RETURN_REASON_LABEL[pendingScan.existing.reason]},{" "}
                    {new Date(pendingScan.existing.returnedAt * 1000).toLocaleDateString("bs-BA")}
                  </span>
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setPendingScan(null); scanInputRef.current?.focus(); }}>
                    U redu
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select value={scanReason} onValueChange={(v) => setScanReason(v as PackageReturnReason)}>
                    <SelectTrigger className="h-9 data-[size=default]:h-9 w-full sm:w-[160px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RETURN_REASONS.map((r) => (
                        <SelectItem key={r} value={r}>{RETURN_REASON_LABEL[r]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={scanNotes}
                    onChange={(e) => setScanNotes(e.target.value)}
                    placeholder="Napomena (opcionalno)"
                    className="flex-1 h-9 text-xs"
                  />
                  <div className="flex gap-2">
                    <Button className="h-9 px-5" onClick={handleConfirm} disabled={scanReturn.isPending}>
                      {scanReturn.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Evidentiraj"}
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-9 text-xs text-muted-foreground"
                      onClick={() => { setPendingScan(null); scanInputRef.current?.focus(); }}
                    >
                      Odustani
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {bulkOpen && (
            <div className="flex flex-col gap-2 pt-1 border-t border-foreground/10">
              <textarea
                value={bulkText}
                onChange={(e) => handleBulkTextChange(e.target.value)}
                placeholder={"Zalijepi tekst sa X ili PPX kodovima...\nX017504263 X017504264 PPX017504265"}
                className="w-full h-20 bg-muted/50 border border-input px-3 py-2 text-xs font-mono resize-y outline-none focus:border-ring focus:bg-transparent transition-colors placeholder:text-muted-foreground/40"
              />
              {bulkCodes.length > 0 && (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="flex-1 min-w-0 text-[10px] text-muted-foreground tabular-nums">
                    {bulkCodes.length} {bulkCodes.length === 1 ? "kod" : "kodova"}
                    <span className="hidden sm:inline text-foreground/30 mx-1.5">|</span>
                    <span className="hidden sm:inline">{bulkCodes.slice(0, 3).join(" · ")}{bulkCodes.length > 3 ? " ..." : ""}</span>
                  </span>
                  <div className="flex gap-2 shrink-0">
                    <Select value={scanReason} onValueChange={(v) => setScanReason(v as PackageReturnReason)}>
                      <SelectTrigger className="h-8 w-[150px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RETURN_REASONS.map((r) => (
                          <SelectItem key={r} value={r}>{RETURN_REASON_LABEL[r]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8" onClick={handleBulkSubmit} disabled={bulkScan.isPending}>
                      {bulkScan.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
                      Evidentiraj {bulkCodes.length}
                    </Button>
                  </div>
                </div>
              )}
              {bulkResult && (
                <div className="flex flex-col gap-1 text-xs">
                  {bulkResult.linked.length > 0 && (
                    <div className="flex items-start gap-2 text-green-700 dark:text-green-400">
                      <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" />
                      <span>
                        Povezani:{" "}
                        {bulkResult.linked
                          .map((e) => `${e.trackingNumber} → #${e.orderId}${e.storeName ? ` (${e.storeName})` : ""}`)
                          .join(", ")}
                      </span>
                    </div>
                  )}
                  {bulkResult.unlinkedCodes.length > 0 && (
                    <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                      <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                      <span>Nepovezani: {bulkResult.unlinkedCodes.join(", ")}</span>
                    </div>
                  )}
                  {bulkResult.duplicates.length > 0 && (
                    <div className="flex items-start gap-2 text-destructive">
                      <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" />
                      <span>Već evidentirani: {bulkResult.duplicates.join(", ")}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-foreground/10">
        {TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => { setActiveTab(tab.value); setPage(1); }}
            className={cn(
              "shrink-0 px-3 pb-2 pt-0.5 text-xs font-medium transition-colors relative",
              "hover:text-foreground",
              activeTab === tab.value
                ? "text-foreground"
                : "text-muted-foreground"
            )}
          >
            {tab.label}
            {tab.value === "sumnjivi" && (suspicious?.length ?? 0) > 0 && (
              <span className="ml-1.5 tabular-nums text-[10px] text-amber-600 dark:text-amber-500">{suspicious!.length}</span>
            )}
            {activeTab === tab.value && (
              <span className="absolute bottom-0 left-3 right-3 h-px bg-foreground" />
            )}
          </button>
        ))}
      </div>

      {activeTab === "evidencija" ? (
        <>
          {/* Filters */}
          <div className="flex gap-2 flex-wrap items-center">
            <Select value={reasonFilter} onValueChange={(v) => { setReasonFilter(v); setPage(1); }}>
              <SelectTrigger className="w-[150px] h-8 text-xs">
                <SelectValue placeholder="Svi razlozi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Svi razlozi</SelectItem>
                {RETURN_REASONS.map((r) => (
                  <SelectItem key={r} value={r}>{RETURN_REASON_LABEL[r]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pretraži tracking ili narudžbu…"
                className="h-8 pl-7 text-xs"
              />
            </div>
            <DatePicker
              value={toDateInput(dateFrom)}
              onChange={(v) => { setDateFrom(fromDateInput(v)); setPage(1); }}
              placeholder="Od"
              className="h-8 text-xs"
            />
            <DatePicker
              value={toDateInput(dateTo)}
              onChange={(v) => { setDateTo(fromDateInput(v)); setPage(1); }}
              placeholder="Do"
              className="h-8 text-xs"
            />
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex flex-col gap-px">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
            </div>
          ) : !entries?.data?.length ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
              <PackageX className="h-6 w-6 opacity-30" />
              <p className="text-xs">Nema zapisa — klikni „Skeniraj" i evidentiraj prvi vraćeni paket</p>
            </div>
          ) : (
            <>
              <div className="border border-foreground/10 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/60 text-muted-foreground">
                      <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Narudžba</th>
                      {showAllStores && (
                        <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden sm:table-cell">Trgovina</th>
                      )}
                      <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden md:table-cell">Tracking / kurir</th>
                      <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Razlog</th>
                      <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Otkazivanje</th>
                      <th className="px-2 py-2 text-right font-medium text-[10px] uppercase tracking-wider">COD iznos</th>
                      <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden sm:table-cell">Datum</th>
                      <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden lg:table-cell">Napomena</th>
                      <th className="px-2 py-2 w-8" aria-label="Akcije" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-foreground/5">
                    {entries.data.map((r) => (
                      <tr key={r.id} className="group transition-colors hover:bg-muted/40">
                        <td className="px-2 py-1.5 font-medium">
                          {r.orderId && r.storeId ? (
                            <button
                              type="button"
                              onClick={() => openOrder(r.orderId!, r.storeId!)}
                              className="text-primary hover:underline"
                            >
                              #{r.orderId}
                            </button>
                          ) : r.orderId ? (
                            `#${r.orderId}`
                          ) : (
                            <span className="text-foreground/20">—</span>
                          )}
                        </td>
                        {showAllStores && (
                          <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell truncate max-w-[120px]">
                            {r.storeName || r.storeId || <span className="text-foreground/20">—</span>}
                          </td>
                        )}
                        <td className="px-2 py-1.5 hidden md:table-cell">
                          <div className="font-mono text-muted-foreground">{r.trackingNumber}</div>
                          {r.credentialName && (
                            <div className="text-[10px] text-muted-foreground/60">{r.credentialName}</div>
                          )}
                        </td>
                        <td className="px-2 py-1.5">
                          <Select value={r.reason} onValueChange={(v) => handleChangeReason(r.id, v as PackageReturnReason)}>
                            <SelectTrigger
                              aria-label="Promijeni razlog"
                              className="h-auto w-auto border-0 bg-transparent p-0 gap-1 shadow-none data-[size=default]:h-auto dark:bg-transparent dark:hover:bg-transparent [&_svg:not([class*='size-'])]:size-3 [&_svg]:opacity-0 [&_svg]:group-hover:opacity-50 [&_svg]:transition-opacity focus-visible:ring-1"
                            >
                              <Badge variant="outline" className={RETURN_REASON_BADGE[r.reason]}>
                                {RETURN_REASON_LABEL[r.reason]}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {RETURN_REASONS.map((reason) => (
                                <SelectItem key={reason} value={reason}>{RETURN_REASON_LABEL[reason]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1.5">
                          {r.cancelStatus ? (
                            <div className="flex items-center gap-1">
                              <Badge
                                variant="outline"
                                className={CANCEL_STATUS_BADGE[r.cancelStatus]}
                                title={r.cancelError || undefined}
                              >
                                {CANCEL_STATUS_LABEL[r.cancelStatus]}
                              </Badge>
                              {canRetryCancel(r.cancelStatus) && (
                                <button
                                  type="button"
                                  onClick={() => handleCancelOrder(r.id, r.orderId, r.cancelStatus === "skipped_paid")}
                                  disabled={cancelingId === r.id}
                                  className="text-muted-foreground hover:text-foreground transition-all disabled:opacity-50 p-0.5 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                                  aria-label="Otkaži narudžbu"
                                  title={r.cancelStatus === "skipped_paid" ? "Ipak otkaži narudžbu" : "Ponovi otkazivanje"}
                                >
                                  {cancelingId === r.id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Ban className="h-3.5 w-3.5" />}
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-foreground/20">—</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                          {r.codAmount != null
                            ? `${r.codAmount.toFixed(2)} ${r.codCurrency ?? "BAM"}`
                            : <span className="text-foreground/20">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell tabular-nums">
                          {new Date(r.returnedAt * 1000).toLocaleDateString("bs-BA")}
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground hidden lg:table-cell truncate max-w-[160px]" title={r.notes}>
                          {r.notes || <span className="text-foreground/20">—</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          <button
                            type="button"
                            onClick={() => handleDelete(r.id)}
                            disabled={deletingId === r.id}
                            className="text-muted-foreground hover:text-red-400 transition-all disabled:opacity-50 p-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                            aria-label="Obriši zapis povrata"
                            title="Obriši zapis povrata"
                          >
                            {deletingId === r.id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {entries.total} ukupno
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                      className="h-7 w-7 flex items-center justify-center border border-foreground/10 text-muted-foreground hover:text-foreground hover:border-foreground/25 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </button>
                    <span className="text-[10px] tabular-nums text-muted-foreground px-2">
                      {page}/{totalPages}
                    </span>
                    <button
                      disabled={!entries.hasNextPage}
                      onClick={() => setPage(page + 1)}
                      className="h-7 w-7 flex items-center justify-center border border-foreground/10 text-muted-foreground hover:text-foreground hover:border-foreground/25 disabled:opacity-30 disabled:pointer-events-none transition-colors"
                    >
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <>
          {/* Šta operator gleda u ovom tabu */}
          <div className="border border-amber-500/30 bg-amber-500/5 p-3 flex gap-2.5 text-xs">
            <Info className="h-3.5 w-3.5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1.5">
              <p className="font-medium">Paketi čiji novac kasni duže od {suspiciousDays} dana</p>
              <p className="text-muted-foreground">
                Ovo nisu evidentirani povrati — ovo su pošiljke poslane prije više od {suspiciousDays} dana
                za koje otkupnina još nije stigla, a niko ih nije evidentirao kao vraćene.
                Za svaki red provjeri šta se stvarno desilo s paketom:
              </p>
              <ul className="text-muted-foreground flex flex-col gap-0.5 list-disc pl-4">
                <li><span className="text-foreground">Novac je u međuvremenu stigao?</span> Označi ga kao primljeno na COD stranici — red će nestati.</li>
                <li><span className="text-foreground">Paket se vratio?</span> Odaberi razlog u koloni „Evidentiraj".</li>
                <li><span className="text-foreground">Ne znaš gdje je?</span> Odaberi „Izgubljen" i provjeri s kurirskom službom.</li>
              </ul>
            </div>
          </div>

          {/* Suspicious threshold */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Prag dana</span>
            <Select value={String(suspiciousDays)} onValueChange={(v) => setSuspiciousDays(Number(v))}>
              <SelectTrigger className="w-[110px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SUSPICIOUS_DAYS_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>{d} dana</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Suspicious table */}
          {suspiciousLoading ? (
            <div className="flex flex-col gap-px">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
            </div>
          ) : !suspicious?.length ? (
            <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
              <CheckCircle2 className="h-6 w-6 opacity-30" />
              <p className="text-xs">Nema sumnjivih paketa starijih od {suspiciousDays} dana</p>
            </div>
          ) : (
            <div className="border border-foreground/10 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/60 text-muted-foreground">
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Narudžba</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden sm:table-cell">Trgovina</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider hidden md:table-cell">Tracking / kurir</th>
                    <th className="px-2 py-2 text-right font-medium text-[10px] uppercase tracking-wider">Iznos</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Starost</th>
                    <th className="px-2 py-2 text-left font-medium text-[10px] uppercase tracking-wider">Evidentiraj</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-foreground/5">
                  {suspicious.map((s) => (
                    <tr key={s.codId} className="transition-colors hover:bg-muted/40">
                      <td className="px-2 py-1.5 font-medium">
                        <button
                          type="button"
                          onClick={() => openOrder(s.orderId, s.storeId)}
                          className="text-primary hover:underline"
                        >
                          #{s.orderId}
                        </button>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground hidden sm:table-cell truncate max-w-[120px]">
                        {s.storeName}
                      </td>
                      <td className="px-2 py-1.5 hidden md:table-cell">
                        <div className="font-mono text-muted-foreground">
                          {s.trackingNumber || <span className="text-foreground/20">—</span>}
                        </div>
                        {s.credentialName && (
                          <div className="text-[10px] text-muted-foreground/60">{s.credentialName}</div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                        {s.amount.toFixed(2)} <span className="text-muted-foreground font-normal">{s.currency}</span>
                      </td>
                      <td className="px-2 py-1.5 text-muted-foreground tabular-nums">{s.ageDays} dana</td>
                      <td className="px-2 py-1.5">
                        <div className="flex items-center gap-1.5">
                          <Select value="" onValueChange={(v) => handleSuspiciousAction(s, v as PackageReturnReason)}>
                            <SelectTrigger className="h-6 w-[130px] text-[10px]" disabled={recordReturn.isPending}>
                              <SelectValue placeholder="Odaberi razlog…" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="lost">{RETURN_REASON_LABEL.lost}</SelectItem>
                              {SUSPICIOUS_RETURN_REASONS.map((reason) => (
                                <SelectItem key={reason} value={reason}>{RETURN_REASON_LABEL[reason]}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {actingCodId === s.codId && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
